import React from "react";
import {
  COUPON_FORM_STEPS,
  COUPON_STATUS,
  COUPON_CATEGORY_OPTIONS,
  COUPON_VALIDATION,
  DISCOUNT_TYPE_OPTIONS,
  ZERO,
} from "constants/coupons";
import {
  getStepConfig,
  isFieldLocked,
  formatDisplayDate,
  formatDiscountFieldValue,
  STATUS_DOT_CLASS_SUFFIX,
} from "helpers/couponFormDisplay";
import { getDiscountTypeLabel } from "helpers/couponFormPreview";
import {
  getLocalIsoDate,
  getMaxExpiryDate,
  getMinExpiryDate,
} from "helpers/couponApi";
import { parsePackageIds, parsePlatformIds } from "helpers/packageIds";
import { getCouponCategoryLabel } from "utils/couponUtils";
import PackageListView from "components/PackageListView/PackageListView";
import PackageSelector from "components/PackageSelector/PackageSelector";
import PlatformListView from "components/PlatformListView/PlatformListView";
import PlatformSelector from "components/PlatformSelector/PlatformSelector";
import { EmptyState } from "widgets";

import CouponFormFieldLabel from "./CouponFormFieldLabel";
import CurrencyAmountBlock from "./CurrencyAmountBlock";
import FormSection from "../FormSection";
import styles from "../CouponForm.module.scss";

const getCurrencyScopeEmptyMessage = ({
  hasSelectedTenants,
  areTenantsConfirmed,
  isCreate,
  isFlat,
}) => {
  if (!hasSelectedTenants) {
    return isFlat
      ? "Select tenants to enter flat discount and MRP amounts for each currency."
      : "Select tenants to configure per-currency discount caps and MRP amounts.";
  }

  if (isCreate && !areTenantsConfirmed) {
    return "Click Done after selecting tenants to configure currency amounts.";
  }

  return "Selected tenants have no currency configured.";
};

const STATUS_DOT_CLASS = {
  [COUPON_STATUS.ACTIVE]: styles[STATUS_DOT_CLASS_SUFFIX.ACTIVE],
  [COUPON_STATUS.INACTIVE]: styles[STATUS_DOT_CLASS_SUFFIX.INACTIVE],
  [COUPON_STATUS.EXPIRED]: styles[STATUS_DOT_CLASS_SUFFIX.EXPIRED],
  [COUPON_STATUS.SCHEDULED]: styles[STATUS_DOT_CLASS_SUFFIX.SCHEDULED],
};

const CouponFormSteps = ({ form, renderers }) => {
  const {
    mode,
    isView,
    isCreate,
    isEdit,
    values,
    errors,
    getStepSubtitle,
    isSectionCollapsed,
    toggleSectionCollapse,
    updateField,
    updateIntegerField,
    updateAmountField,
    handlePackagesChange,
    handlePlatformsChange,
    computedStatus,
    statusLabel,
    validityDays,
    basicPackages,
    isBasicPackagesLoading,
    isBasicPackagesError,
    basicPackagesError,
    refetchBasicPackages,
    verticalOptions,
    tenantOptions,
    currencyOptions,
    isVerticalOptionsLoading,
    isTenantOptionsLoading,
    isVerticalOptionsError,
    isTenantOptionsError,
    verticalOptionsError,
    tenantOptionsError,
    refetchVerticalOptions,
    refetchTenantOptions,
    handleVerticalChange,
    handleTenantsChange,
    handleConfirmTenants,
    handleEditTenants,
    areTenantsConfirmed,
    hasSelectedTenants,
    hasSelectedVertical,
    hasDiscountType,
    isPercentageDiscount,
    selectedCurrencyIds,
    updateCurrencySettingField,
    isTenantDropdownDisabled,
    isVerticalDropdownDisabled,
  } = form;

  const {
    renderFieldError,
    renderFieldInput,
    renderReadOnlyField,
    renderSelectField,
    renderTenantCheckboxField,
    renderYesNoToggle,
    renderAmountDecimalHint,
    renderDiscountValueHints,
  } = renderers;

  return (
    <>
      <FormSection
        stepId={1}
        title={getStepConfig(1, COUPON_FORM_STEPS).title}
        subtitle={getStepSubtitle(1)}
        iconName={getStepConfig(1, COUPON_FORM_STEPS).iconName}
        isCollapsed={isSectionCollapsed(1)}
        onToggle={toggleSectionCollapse}
      >
        <div className={styles.fieldGrid}>
          <div className={`${styles.fieldGroup} ${styles.fieldGroupFull}`}>
            <CouponFormFieldLabel required>Discount Type</CouponFormFieldLabel>
            {renderFieldInput(
              "discountType",
              isFieldLocked(mode, "discountType") ? (
                <span>{getDiscountTypeLabel(values.discountType)}</span>
              ) : (
                <select
                  value={values.discountType ?? ""}
                  onChange={(e) =>
                    updateField("discountType", e.target.value)
                  }
                >
                  {isCreate && (
                    <option value="">Select discount type</option>
                  )}
                  {DISCOUNT_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              ),
              isFieldLocked(mode, "discountType"),
              isFieldLocked(mode, "discountType")
            )}
            {renderFieldError("discountType")}
            {!isView && !isFieldLocked(mode, "discountType") && (
              <p className={styles.fieldHint}>
                Percentage uses one discount rate across selected tenants. Flat
                amounts are entered per currency derived from those tenants.
              </p>
            )}
          </div>
          {isCreate &&
            renderSelectField({
            fieldName: "verticalId",
            label: "Vertical",
            value: values.verticalId,
            options: verticalOptions,
            placeholder: hasDiscountType
              ? "Select vertical"
              : "Select discount type first",
            disabled: isVerticalDropdownDisabled,
            isLoading: isVerticalOptionsLoading,
            isError: isVerticalOptionsError,
            errorMessage: verticalOptionsError,
            onRetry: refetchVerticalOptions,
            onChange: handleVerticalChange,
          })}
          {renderTenantCheckboxField({
            fieldName: "tenantIds",
            label: "Tenant",
            className: styles.fieldGroupFull,
            selectedIds: values.tenantIds,
            options: tenantOptions,
            showTenantCurrencyBadge: true,
            placeholder: !hasDiscountType
              ? "Select discount type first"
              : isCreate && !hasSelectedVertical
                ? "Select a vertical first"
                : isTenantOptionsLoading
                  ? "Loading tenants..."
                  : "Select tenants",
            disabled: isTenantDropdownDisabled || isEdit || isView,
            isLoading:
              isCreate &&
              hasSelectedVertical &&
              hasDiscountType &&
              isTenantOptionsLoading,
            isError:
              isCreate &&
              hasSelectedVertical &&
              hasDiscountType &&
              isTenantOptionsError,
            errorMessage: tenantOptionsError,
            onRetry: refetchTenantOptions,
            onChange: handleTenantsChange,
            emptyHint:
              hasSelectedVertical &&
              hasDiscountType &&
              !isTenantOptionsLoading &&
              !isTenantOptionsError &&
              tenantOptions.length === ZERO
                ? "No tenants are available for this vertical."
                : null,
            footer: (
              <>
                {isCreate &&
                  !isView &&
                  hasSelectedTenants &&
                  !areTenantsConfirmed && (
                    <div className={styles.tenantConfirmRow}>
                      <button
                        type="button"
                        className={styles.tenantConfirmButton}
                        onClick={handleConfirmTenants}
                      >
                        Done
                      </button>
                      <p className={styles.fieldHint}>
                        Confirm tenants to load packages and show currency
                        amount fields.
                      </p>
                    </div>
                  )}
                {isCreate && !isView && areTenantsConfirmed && (
                  <div className={styles.tenantConfirmRow}>
                    <p className={styles.tenantConfirmedMessage}>
                      Tenants confirmed
                      {selectedCurrencyIds.length > ZERO
                        ? ` · ${selectedCurrencyIds.length} currenc${
                            selectedCurrencyIds.length === 1 ? "y" : "ies"
                          }`
                        : ""}
                    </p>
                    <button
                      type="button"
                      className={styles.tenantEditButton}
                      onClick={handleEditTenants}
                    >
                      Edit tenants
                    </button>
                  </div>
                )}
                {isEdit && !isView && (
                  <p className={styles.fieldHint}>
                    Tenants and currency cannot be changed after creation.
                  </p>
                )}
              </>
            ),
          })}
          <div className={`${styles.fieldGroup} ${styles.fieldGroupFull}`}>
            <CouponFormFieldLabel required>Coupon Code</CouponFormFieldLabel>
            {isView ? (
              renderReadOnlyField("code", values.code)
            ) : (
              renderFieldInput(
                "code",
                <input
                  type="text"
                  value={values.code}
                  onChange={(e) =>
                    updateField("code", e.target.value.toUpperCase())
                  }
                  disabled={isFieldLocked(mode, "code")}
                  placeholder="e.g. ALLPACK5008765"
                  maxLength={COUPON_VALIDATION.MAX_COUPON_CODE_LENGTH}
                />
              )
            )}
            {renderFieldError("code")}
            {!isCreate && !isView && (
              <p className={styles.fieldHint}>
                Coupon code cannot be changed.
              </p>
            )}
            {isCreate && (
              <p className={styles.fieldHint}>
                Letters, numbers, hyphen, and underscore only. Max{" "}
                {COUPON_VALIDATION.MAX_COUPON_CODE_LENGTH} characters.
              </p>
            )}
          </div>
          <div className={styles.fieldGroup}>
            <CouponFormFieldLabel required>Coupon Category</CouponFormFieldLabel>
            {renderFieldInput(
              "category",
              isView || isFieldLocked(mode, "category") ? (
                <span>{getCouponCategoryLabel(values.category)}</span>
              ) : (
                <select
                  value={values.category ?? ""}
                  onChange={(e) => updateField("category", e.target.value)}
                >
                  <option value="">Select category</option>
                  {COUPON_CATEGORY_OPTIONS.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              ),
              isView || isFieldLocked(mode, "category"),
              isView || isFieldLocked(mode, "category")
            )}
            {renderFieldError("category")}
            {isEdit && !isView && (
              <p className={styles.fieldHint}>
                Coupon category cannot be changed.
              </p>
            )}
          </div>
          <div className={`${styles.fieldGroup} ${styles.fieldGroupFull}`}>
            <CouponFormFieldLabel required>Remarks</CouponFormFieldLabel>
            {isView ? (
              renderReadOnlyField("remarks", values.remarks)
            ) : (
              renderFieldInput(
                "remarks",
                <textarea
                  className={styles.remarksTextarea}
                  value={values.remarks ?? ""}
                  onChange={(e) => updateField("remarks", e.target.value)}
                  maxLength={COUPON_VALIDATION.MAX_COUPON_REMARKS_LENGTH}
                  placeholder="Internal remarks for coupon context"
                  rows={3}
                />,
                null,
                true,
                styles.fieldInputMultiline
              )
            )}
            {renderFieldError("remarks")}
            {!isView && (
              <p className={styles.fieldHint}>
                {(values.remarks ?? "").length}/
                {COUPON_VALIDATION.MAX_COUPON_REMARKS_LENGTH} characters
              </p>
            )}
          </div>
        </div>
      </FormSection>

      <FormSection
        stepId={2}
        title={getStepConfig(2, COUPON_FORM_STEPS).title}
        subtitle={getStepSubtitle(2)}
        iconName={getStepConfig(2, COUPON_FORM_STEPS).iconName}
        isCollapsed={isSectionCollapsed(2)}
        onToggle={toggleSectionCollapse}
      >
        <div className={styles.fieldGrid}>
          <div className={styles.fieldGroup}>
            <CouponFormFieldLabel required>Effective Date</CouponFormFieldLabel>
            {renderFieldInput(
              "effectiveDate",
              isFieldLocked(mode, "effectiveDate") ? (
                <span>{formatDisplayDate(values.effectiveDate)}</span>
              ) : (
                <input
                  type="date"
                  value={values.effectiveDate || ""}
                  min={isCreate ? getLocalIsoDate() : undefined}
                  onChange={(e) =>
                    updateField("effectiveDate", e.target.value)
                  }
                />
              )
            )}
            {renderFieldError("effectiveDate")}
            {!isCreate && !isView && (
              <p className={styles.fieldHint}>
                Effective date cannot be changed.
              </p>
            )}
            {isCreate && (
              <p className={styles.fieldHint}>
                Must be today or within{" "}
                {COUPON_VALIDATION.MAX_ALLOWED_EFFECTIVE_DATE_OFFSET_DAYS}{" "}
                days.
              </p>
            )}
          </div>
          <div className={styles.fieldGroup}>
            <CouponFormFieldLabel required>Expiry Date</CouponFormFieldLabel>
            {isView ? (
              renderReadOnlyField(
                "expiryDate",
                formatDisplayDate(values.expiryDate)
              )
            ) : (
              renderFieldInput(
                "expiryDate",
                <input
                  type="date"
                  value={values.expiryDate}
                  min={
                    values.effectiveDate
                      ? getMinExpiryDate(values.effectiveDate)
                      : undefined
                  }
                  max={
                    values.effectiveDate
                      ? getMaxExpiryDate(values.effectiveDate)
                      : undefined
                  }
                  onChange={(e) => updateField("expiryDate", e.target.value)}
                />
              )
            )}
            {renderFieldError("expiryDate")}
            {!isCreate && !isView && (
              <p className={styles.fieldHint}>
                You can update the expiry date.
              </p>
            )}
            {isCreate && (
              <p className={styles.fieldHint}>
                Must be 1–
                {COUPON_VALIDATION.MAX_EXPIRY_DATE_AFTER_EFFECTIVE_DAYS} days
                after effective date.
              </p>
            )}
          </div>
          {!isCreate && (
            <div className={styles.fieldGroup}>
              <CouponFormFieldLabel>Status</CouponFormFieldLabel>
              <div className={styles.statusSelectWrap}>
                <span
                  className={`${styles.statusDot} ${
                    STATUS_DOT_CLASS[computedStatus] ?? styles.statusDotActive
                  }`}
                />
                <span>{statusLabel}</span>
              </div>
              {!isView && (
                <p className={styles.fieldHint}>
                  Status is determined from the effective and expiry dates.
                </p>
              )}
            </div>
          )}
          {validityDays !== null && (
            <div className={`${styles.fieldGroup} ${styles.validitySummary}`}>
              <CouponFormFieldLabel>Validity Summary</CouponFormFieldLabel>
              <div className={styles.validitySummaryBox}>
                <span className={styles.validitySummaryValue}>
                  {validityDays} day{validityDays === 1 ? "" : "s"}
                </span>
                <span className={styles.validitySummaryHint}>
                  Between effective and expiry date
                </span>
              </div>
            </div>
          )}
        </div>
      </FormSection>

      <FormSection
        stepId={3}
        title={getStepConfig(3, COUPON_FORM_STEPS).title}
        subtitle={getStepSubtitle(3)}
        iconName={getStepConfig(3, COUPON_FORM_STEPS).iconName}
        isCollapsed={isSectionCollapsed(3)}
        onToggle={toggleSectionCollapse}
      >
        <div className={styles.fieldGrid}>
          {isPercentageDiscount && (
            <div className={styles.fieldGroup}>
              <CouponFormFieldLabel required>
                Discount Percentage
              </CouponFormFieldLabel>
              {renderFieldInput(
                "discountValue",
                isFieldLocked(mode, "discountValue") ? (
                  <span>
                    {formatDiscountFieldValue(values, currencyOptions)}
                  </span>
                ) : (
                  <input
                    type="text"
                    inputMode="decimal"
                    value={values.discountValue ?? ""}
                    placeholder="Enter discount percentage"
                    onChange={(e) =>
                      updateAmountField("discountValue", e.target.value)
                    }
                  />
                )
              )}
              {renderFieldError("discountValue")}
              {renderDiscountValueHints()}
              {isEdit && !isView && (
                <p className={styles.fieldHint}>
                  Discount percentage cannot be changed after creation.
                </p>
              )}
            </div>
          )}
          <div className={`${styles.fieldGroup} ${styles.fieldGroupFull}`}>
            {selectedCurrencyIds.length === ZERO ? (
              <EmptyState
                message={getCurrencyScopeEmptyMessage({
                  hasSelectedTenants,
                  areTenantsConfirmed,
                  isCreate,
                  isFlat: !isPercentageDiscount,
                })}
              />
            ) : (
              <div className={styles.currencyEligibilityList}>
                {selectedCurrencyIds.map((currencyId) => (
                  <CurrencyAmountBlock
                    key={currencyId}
                    currencyId={currencyId}
                    currencyOptions={currencyOptions}
                    settings={values.currencySettings?.[currencyId]}
                    fieldErrors={errors.currencySettings?.[currencyId] ?? {}}
                    isView={isView}
                    discountType={values.discountType}
                    lockDiscountAmount={isEdit}
                    renderFieldInput={renderFieldInput}
                    renderAmountDecimalHint={renderAmountDecimalHint}
                    updateCurrencySettingField={updateCurrencySettingField}
                  />
                ))}
              </div>
            )}
            {typeof errors.currencySettings === "string" ? (
              <p className={styles.fieldError}>{errors.currencySettings}</p>
            ) : null}
            {!isView &&
              !isEdit &&
              areTenantsConfirmed &&
              selectedCurrencyIds.length > 1 && (
                <p className={styles.fieldHint}>
                  {isPercentageDiscount
                    ? "One coupon will be created with per-currency max amounts for all selected tenants."
                    : "One coupon will be created per currency when multiple currencies are selected (code suffix by currency)."}
                </p>
              )}
            {isEdit && !isView && !isPercentageDiscount && (
              <p className={styles.fieldHint}>
                Flat discount amount cannot be changed after creation.
              </p>
            )}
          </div>
        </div>
      </FormSection>

      <FormSection
        stepId={4}
        title={getStepConfig(4, COUPON_FORM_STEPS).title}
        subtitle={getStepSubtitle(4)}
        iconName={getStepConfig(4, COUPON_FORM_STEPS).iconName}
        isCollapsed={isSectionCollapsed(4)}
        onToggle={toggleSectionCollapse}
      >
        <div className={styles.fieldGrid}>
          <div className={styles.fieldGroup}>
            <CouponFormFieldLabel required>Total Coupon Count</CouponFormFieldLabel>
            {isView ? (
              renderReadOnlyField("totalCouponCount", values.totalCouponCount)
            ) : (
              renderFieldInput(
                "totalCouponCount",
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="Enter count"
                  value={values.totalCouponCount ?? ""}
                  onChange={(event) =>
                    updateIntegerField("totalCouponCount", event.target.value, {
                      min: isEdit
                        ? Math.max(
                            values.usedCount ?? ZERO,
                            COUPON_VALIDATION.MIN_TOTAL_COUPON_COUNT
                          )
                        : COUPON_VALIDATION.MIN_TOTAL_COUPON_COUNT,
                      max: COUPON_VALIDATION.MAX_TOTAL_COUPON_COUNT,
                      blockBelowMin: true,
                    })
                  }
                />
              )
            )}
            {renderFieldError("totalCouponCount")}
            {!isView && (
              <p className={styles.fieldHint}>
                Must be greater than 0. Maximum{" "}
                {COUPON_VALIDATION.MAX_TOTAL_COUPON_COUNT.toLocaleString()}.
                {isEdit && (values.usedCount ?? ZERO) > ZERO
                  ? ` Must be at least ${(values.usedCount ?? ZERO).toLocaleString()} (already used).`
                  : ""}
              </p>
            )}
          </div>
          <div className={styles.fieldGroup}>
            <CouponFormFieldLabel required>Applicable multiple times</CouponFormFieldLabel>
            {renderYesNoToggle("isApplicableMultipleTimes")}
            {renderFieldError("isApplicableMultipleTimes")}
          </div>
          <div className={styles.fieldGroup}>
            <CouponFormFieldLabel required>Is Applicable For Single User</CouponFormFieldLabel>
            {renderYesNoToggle("isApplicableForSingleUser", {
              disabled: isEdit,
            })}
            {renderFieldError("isApplicableForSingleUser")}
            {isEdit && !isView && (
              <p className={styles.fieldHint}>
                Single-user applicability cannot be changed.
              </p>
            )}
          </div>
          {values.isApplicableForSingleUser && (
            <div className={styles.fieldGroup}>
              <CouponFormFieldLabel required>UAM ID</CouponFormFieldLabel>
              {isView || isEdit ? (
                renderReadOnlyField("uamId", values.uamId)
              ) : (
                renderFieldInput(
                  "uamId",
                  <input
                    type="number"
                    value={values.uamId ?? ""}
                    onChange={(e) =>
                      updateIntegerField("uamId", e.target.value)
                    }
                  />
                )
              )}
              {isEdit && !isView && (
                <p className={styles.fieldHint}>
                  UAM ID cannot be changed after creation.
                </p>
              )}
              {renderFieldError("uamId")}
            </div>
          )}
        </div>
      </FormSection>

      <FormSection
        stepId={5}
        title={getStepConfig(5, COUPON_FORM_STEPS).title}
        subtitle={getStepSubtitle(5)}
        iconName={getStepConfig(5, COUPON_FORM_STEPS).iconName}
        isCollapsed={isSectionCollapsed(5)}
        onToggle={toggleSectionCollapse}
      >
        {isCreate || isEdit ? (
          <div className={styles.fieldGrid}>
            <div className={`${styles.fieldGroup} ${styles.fieldGroupFull}`}>
              <CouponFormFieldLabel required>Applicable Packages</CouponFormFieldLabel>
              {!hasSelectedTenants ? (
                <EmptyState message="Select at least one tenant in Basic Details to load packages." />
              ) : isCreate && !areTenantsConfirmed ? (
                <EmptyState message="Click Done after selecting tenants to load packages." />
              ) : (
                <PackageSelector
                  selectAll={Boolean(values.selectAllPackages)}
                  selectedIds={parsePackageIds(values.packageIds)}
                  onChange={handlePackagesChange}
                  error={errors.packageIds || errors.selectAllPackages}
                  packages={basicPackages}
                  isLoading={isBasicPackagesLoading}
                  isError={isBasicPackagesError}
                  fetchError={basicPackagesError}
                  onRefetch={refetchBasicPackages}
                />
              )}
            </div>
          </div>
        ) : (
          <PackageListView
            selectAll={Boolean(values.selectAllPackages)}
            selectedIds={parsePackageIds(values.packageIds)}
            viewMode
            packages={basicPackages}
            isLoading={isBasicPackagesLoading}
            isError={isBasicPackagesError}
            fetchError={basicPackagesError}
            onRefetch={refetchBasicPackages}
          />
        )}
      </FormSection>

      <FormSection
        stepId={6}
        title={getStepConfig(6, COUPON_FORM_STEPS).title}
        subtitle={getStepSubtitle(6)}
        iconName={getStepConfig(6, COUPON_FORM_STEPS).iconName}
        isCollapsed={isSectionCollapsed(6)}
        onToggle={toggleSectionCollapse}
      >
        {isCreate || isEdit ? (
          <div className={styles.fieldGrid}>
            <div className={`${styles.fieldGroup} ${styles.fieldGroupFull}`}>
              <CouponFormFieldLabel required>Applicable Platforms</CouponFormFieldLabel>
              <PlatformSelector
                selectAll={Boolean(values.selectAllPlatforms)}
                selectedIds={parsePlatformIds(values.platformIds)}
                onChange={handlePlatformsChange}
                error={errors.platformIds || errors.selectAllPlatforms}
              />
            </div>
          </div>
        ) : (
          <PlatformListView
            selectAll={Boolean(values.selectAllPlatforms)}
            selectedIds={parsePlatformIds(values.platformIds)}
            viewMode
          />
        )}
      </FormSection>
    </>
  );
};

export default CouponFormSteps;
